import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"

import { useChatDrawer } from "./ChatDrawerContext"

const DRAWER_WIDTH = 360
const TRANSITION_DURATION = 100

ChatDrawer.propTypes = {
  children: PropTypes.node.isRequired,
}

export function ChatDrawer({ children }) {
  const { isOpen, route, openDrawer, closeDrawer } = useChatDrawer()

  const shouldRenderChildren = useShouldRenderChildren(
    isOpen,
    TRANSITION_DURATION,
  )

  useEffect(() => {
    if (route && !isOpen) openDrawer()
  }, [route, isOpen, openDrawer])

  // Keys pressed inside the iframe never reach this document; chat handles
  // those itself via CLOSE_DRAWER.
  const closeOnEscape = (event) => {
    if (event.key !== "Escape" || event.defaultPrevented) return
    event.preventDefault()
    closeDrawer()
  }

  return (
    <React.Fragment>
      <style>{chatDrawerStyles}</style>
      {/* Escape on the dialog element is the standard dialog pattern; jsx-a11y treats role="dialog" as non-interactive. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={`ChatDrawer ${isOpen ? "is-open" : ""}`}
        id="chat-drawer"
        role="dialog"
        aria-label="Chat"
        tabIndex={-1}
        inert={!isOpen}
        onKeyDown={closeOnEscape}
      >
        {shouldRenderChildren && children}
      </div>
    </React.Fragment>
  )
}

const chatDrawerStyles = `
  .ChatDrawer {
    background: var(--color-tint9);
    border-left: 1px solid var(--color-tint5);
    box-shadow: -2px 0 6px rgba(0,0,0,0.1);
    height: 100%;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 1001; /* Thanks to .Header for setting its z-index to 1000 🙃 */
    visibility: hidden;
    opacity: 0;
  }

  .ChatDrawer.is-open {
    visibility: visible;
    opacity: 1;
  }

  @media (min-width: 600px) {
    .ChatDrawer {
      left: auto;
      width: ${DRAWER_WIDTH}px;
      transform: translateX(${DRAWER_WIDTH}px);
      transition: transform ${TRANSITION_DURATION}ms ease-in-out;
      visibility: visible;
      opacity: 1;
    }

    .ChatDrawer.is-open {
      transform: translateX(0);
    }
  }
`

function useShouldRenderChildren(isOpen, delay) {
  const [shouldRenderChildren, setShouldRenderChildren] = useState(isOpen)

  useEffect(() => {
    if (isOpen) {
      setShouldRenderChildren(true)
    } else {
      const timer = setTimeout(() => setShouldRenderChildren(false), delay)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  return shouldRenderChildren
}
